// Copyright (c) 2021 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import type { JSX } from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, IntlContext, IntlShape } from "react-intl";

import { RoutedProps, withRouter } from "view/reactrouterinterceptor";
import { isSet, round } from "shared/utils/utils";
import { ICloudParameter, IVirtAssign } from "shared/types/cloudtypes";
import { ICharger } from "shared/types/parameter";
import {
  makeSortHeader,
  tabClickHandler,
  getVirtualSiteSourceName,
  renderVirtualSiteIcon,
} from "guiutils";
import { ActionType, IAction, raiseAction, IActionSortTable } from "actions";
import { IState } from "states/states";
import { IStateDataCharger } from "states/datastates";
import { ITableSort } from "states/guistates";
import { Site } from "states/sitestates";

import EnlineChargerView from "view/comp/EnlineChargerView";

// sort key of table
const TABLE_NAME = "t_enline_chargers";

interface IChargersProps {
  chargerConfigs: ICharger[];
  chargerData: IStateDataCharger[] | undefined;
  selectedCharger: number | undefined; // for enline chargers: array index
  tableSort: ITableSort | undefined;
  virtualChargers: IVirtAssign[];
  siteId: number | undefined;
  siteList: Site[] | undefined;
}

const mapStateToProps = (state: IState) => {
  const props: IChargersProps = {
    chargerConfigs: state.para.parameter?.charger ?? [],
    chargerData: state.data.charger,
    selectedCharger: state.router.charger,
    tableSort: state.gui.tableSort[TABLE_NAME],
    virtualChargers:
      (state.para.parameter as ICloudParameter | undefined)?.virtualSite?.charger ?? [],
    siteId: state.cached.selectedSite,
    siteList: state.site.siteList,
  };
  return props;
};

interface IChargersDispatch {
  onSort: (sort: IActionSortTable) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IChargersDispatch = {
    onSort: (sort: IActionSortTable) => {
      dispatch(raiseAction(ActionType.ACT_SORT_TABLE, sort));
    },
  };
  return dispatchProps;
};

const messages = defineMessages<{
  virtualSite: { site: string };
}>({
  virtualSite: {
    id: "This item's data originates from {site}",
  },
});

class ChargersComponent extends React.Component<IChargersProps & IChargersDispatch & RoutedProps> {
  static contextType = IntlContext;
  declare context: IntlShape;

  public render() {
    const selectedCharger =
      isSet(this.props.selectedCharger) ?
        this.props.chargerData?.[this.props.selectedCharger]
      : undefined;
    return (
      <div id="enline-chargers">
        <h1>
          <FormattedMessage id="Energy Management Chargers" />
        </h1>
        {this.renderEnlineChargerList()}
        {isSet(selectedCharger) && selectedCharger.isConnected === true && (
          <div id="chargercontainer">
            <EnlineChargerView />
          </div>
        )}
      </div>
    );
  }

  private renderEnlineChargerList() {
    const chargerItems = this.getSortedChargerIndices().map((curIndex) => {
      return this.getChargerRow(curIndex);
    });

    return (
      <table className="datatable fullwidthwhite">
        <thead>
          <tr>
            {this.makeSortHeader("name", <FormattedMessage id="Name" />)}
            {this.makeSortHeader("type", <FormattedMessage id="Type" />, ["optional-lg"])}
            {this.makeSortHeader("ipAddress", <FormattedMessage id="Address" />, ["optional-lg"])}
            {this.makeSortHeader("reqMaxPower", <FormattedMessage id="Max. Power" />, [
              "value-col",
            ])}
            {this.makeSortHeader("reqMaxCurrent", <FormattedMessage id="Max. Current" />, [
              "value-col",
            ])}
            {this.makeSortHeader("curP", <FormattedMessage id="Power" />, ["value-col"])}
            {this.makeSortHeader("curI", <FormattedMessage id="Current" />, [
              "value-col",
              "optional-lg",
            ])}
            {this.makeSortHeader("eTotal", <FormattedMessage id="Energy" />, [
              "value-col",
              "optional-lg",
            ])}
            {this.makeSortHeader("eSession", <FormattedMessage id="Session" />, [
              "value-col",
              "optional-lg",
            ])}
            {this.makeSortHeader("plugState", <FormattedMessage id="State" />)}
          </tr>
        </thead>
        <tbody>{chargerItems}</tbody>
      </table>
    );
  }

  // this function is just a short-cut to reduce boilerplate
  private makeSortHeader(
    column: keyof IStateDataCharger | "type" | "name" | "ipAddress",
    caption: JSX.Element,
    classes?: string[],
    title?: string,
  ) {
    return makeSortHeader(
      TABLE_NAME,
      column,
      caption,
      this.props.tableSort ?? { column: "name", isSortAscending: true },
      this.props.onSort,
      classes,
      title,
    );
  }

  private getSortedChargerIndices() {
    const sortCol = (this.props.tableSort?.column ?? "name") as
      keyof IStateDataCharger | "type" | "name" | "ipAddress";
    // skip inactive chargers; keep the config index, as the data array is aligned to it
    const indices = Array.from(Array(this.props.chargerConfigs.length).keys()).filter(
      (curIndex) => this.props.chargerConfigs[curIndex].isEnabled !== false,
    );
    return indices.sort((a, b) => {
      let keyA: string | number | boolean | undefined;
      let keyB: string | number | boolean | undefined;
      // special handling for special columns
      if (sortCol === "name" || sortCol === "type" || sortCol === "ipAddress") {
        keyA = this.props.chargerConfigs[a][sortCol];
        keyB = this.props.chargerConfigs[b][sortCol];
      } else {
        keyA = this.props.chargerData?.[a]?.[sortCol!] as string | number | boolean | undefined;
        keyB = this.props.chargerData?.[b]?.[sortCol!] as string | number | boolean | undefined;
      }
      const sort =
        isSet(keyA) && isSet(keyB) ?
          keyA > keyB ?
            1
          : -1
        : isSet(keyB) ? 1
        : -1;
      return this.props.tableSort?.isSortAscending !== false ? sort : -sort;
    });
  }

  private getChargerRow(index: number) {
    const curConfig = this.props.chargerConfigs[index];
    const curData =
      this.props.chargerData && this.props.chargerData.length > index ?
        this.props.chargerData[index]
      : undefined;
    const classes: string[] = [];
    if (index === this.props.selectedCharger) {
      classes.push("selected");
    }
    if (curData?.isConnected === false) {
      classes.push("disconnected");
    }
    if (curData?.errorChargerRW) {
      classes.push("rwerror");
    }
    const virtualSiteName = getVirtualSiteSourceName(
      this.props.virtualChargers,
      index,
      this.props.siteId,
      this.props.siteList,
    );
    return (
      <tr
        key={index}
        tabIndex={0}
        onKeyDown={tabClickHandler}
        className={classes.join(" ")}
        onClick={() => this.props.navigate(`?charger=${index}`)}
      >
        <td>
          {curConfig.name}
          {virtualSiteName &&
            renderVirtualSiteIcon(
              this.context.formatMessage(messages.virtualSite, { site: virtualSiteName }),
            )}
        </td>
        <td className="optional-lg">{curConfig.type}</td>
        <td className="optional-lg">{curConfig.ipAddress}</td>
        <td className="value-col">
          {isSet(curData?.reqMaxPower) && `${round(curData!.reqMaxPower, 1)} kW`}
        </td>
        <td className="value-col">
          {isSet(curData?.reqMaxCurrent) && `${round(curData!.reqMaxCurrent, 2)} A`}
        </td>
        <td className="value-col">{isSet(curData?.curP) && `${round(curData!.curP, 1)} kW`}</td>
        <td className="value-col optional-lg">
          {isSet(curData?.curI) &&
            `${curData!.curI
              .filter((curValue) => isSet(curValue) && !isNaN(curValue))
              .map((curValue) => {
                return round(curValue, 2);
              })
              .join(", ")} A`}
        </td>
        <td className="value-col optional-lg">
          {isSet(curData?.eTotal) && `${round(curData!.eTotal, 0)} kWh`}
        </td>
        <td className="value-col optional-lg">
          {isSet(curData?.eSession) && `${round(curData!.eSession, 1)} kWh`}
        </td>
        <td>
          {isSet(curData?.plugState) && (
            <>
              <FormattedMessage id={`plugstate_${curData!.plugState}`} />,{" "}
            </>
          )}
          {curData?.plugIsLocked === true && (
            <>
              <FormattedMessage id="locked" />,{" "}
            </>
          )}
          {isSet(curData?.chargingState) && (
            <FormattedMessage id={`chargingstate_${curData!.chargingState}`} />
          )}
        </td>
      </tr>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(ChargersComponent));
